// useLanePointEditor.js — Manages rubber-band drag select + group move for
// lane waypoints in PathSelectorMap. Self-contained: creates and destroys its
// own Google Maps markers imperatively, does not interfere with the path
// waypoint markers system.

import { useEffect, useRef } from 'react';
import { catmullRomSpline, SPLINE_SAMPLES_EDIT } from './splineUtils';

// ─── Icon helpers ─────────────────────────────────────────────────────────────

function getLaneEditMarkerIcon(g, state) {
  const configs = {
    normal:   { scale: 5,  fillColor: '#fef08a',  strokeColor: '#78716c', strokeWeight: 1.5, fillOpacity: 1 },
    selected: { scale: 7,  fillColor: '#facc15',  strokeColor: '#000000', strokeWeight: 2,   fillOpacity: 1 },
    hover:    { scale: 6,  fillColor: '#fef08a',  strokeColor: '#ffffff', strokeWeight: 2,   fillOpacity: 1 },
  };
  const cfg = configs[state] ?? configs.normal;
  return {
    path: g.SymbolPath.CIRCLE,
    ...cfg,
  };
}

// ─── Pixel math helpers ───────────────────────────────────────────────────────

/** Convert a Google Maps LatLng to pixel position relative to the map div. */
function latLngToMapPixel(map, latLng) {
  const proj = map.getProjection();
  if (!proj) return null;
  const bounds = map.getBounds();
  if (!bounds) return null;
  const scale = Math.pow(2, map.getZoom());
  const worldNE = proj.fromLatLngToPoint(bounds.getNorthEast());
  const worldSW = proj.fromLatLngToPoint(bounds.getSouthWest());
  const worldPt = proj.fromLatLngToPoint(latLng);
  return {
    x: (worldPt.x - worldSW.x) * scale,
    y: (worldPt.y - worldNE.y) * scale,
  };
}

/** Check whether a pixel position lies inside a rect (all pixel coords). */
function pixelInRect(px, rect) {
  return px.x >= rect.left && px.x <= rect.right && px.y >= rect.top && px.y <= rect.bottom;
}

// ─── Marker management ────────────────────────────────────────────────────────

// ─── Insert index helper (spline-aware) ───────────────────────────────────────

/**
 * Given a click at (lat, lng) on the spline-interpolated path, find which
 * control-point segment it belongs to and return the insert index for splicing.
 * Works because catmullRomSpline emits exactly `samplesPerSegment` points per
 * segment, so Math.floor(sampleIndex / samplesPerSegment) gives the segment.
 */
function findInsertIndexOnSpline(splinePath, waypointCount, samplesPerSegment, lat, lng) {
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < splinePath.length; i++) {
    const d = Math.hypot(splinePath[i].lat - lat, splinePath[i].lng - lng);
    if (d < bestDist) { bestDist = d; bestIdx = i; }
  }
  const segIdx = Math.min(
    Math.floor(bestIdx / samplesPerSegment),
    waypointCount - 2
  );
  return segIdx + 1;
}

// ─── Marker management ────────────────────────────────────────────────────────

function buildLaneEditMarkers({
  map,
  waypoints,
  selectedIndices,
  isDraggingGroupRef,
  ctrlHeldRef,
  editMarkersRef,
  onMarkerClick,
  onMarkerDragEnd,
  onMarkerRemove,
}) {
  const g = window.google.maps;

  // Clear previous markers
  editMarkersRef.current.forEach(({ marker, listeners }) => {
    listeners.forEach((l) => g.event.removeListener(l));
    marker.setMap(null);
  });
  editMarkersRef.current = [];

  waypoints.forEach((wp, i) => {
    const isSelected = selectedIndices.includes(i);
    const marker = new g.Marker({
      position: { lat: wp.lat, lng: wp.lng },
      map,
      icon: getLaneEditMarkerIcon(g, isSelected ? 'selected' : 'normal'),
      draggable: true,
      cursor: 'grab',
      zIndex: isSelected ? 2 : 1,
    });

    const listeners = [];
    let dragStartPos = null;

    // Hover
    listeners.push(g.event.addListener(marker, 'mouseover', () => {
      if (!isDraggingGroupRef.current) {
        marker.setIcon(getLaneEditMarkerIcon(g, 'hover'));
      }
    }));
    listeners.push(g.event.addListener(marker, 'mouseout', () => {
      const sel = editMarkersRef.current[i]?._isSelected;
      if (!isDraggingGroupRef.current) {
        marker.setIcon(getLaneEditMarkerIcon(g, sel ? 'selected' : 'normal'));
      }
    }));

    // Click to select
    listeners.push(g.event.addListener(marker, 'click', (e) => {
      if (isDraggingGroupRef.current) return;
      if (ctrlHeldRef.current) return; // ctrl is for lasso
      const shiftKey = e?.domEvent?.shiftKey ?? false;
      onMarkerClick(i, shiftKey);
    }));

    // Right-click to remove
    listeners.push(g.event.addListener(marker, 'rightclick', () => {
      onMarkerRemove?.(i);
    }));

    // Drag group move
    listeners.push(g.event.addListener(marker, 'dragstart', () => {
      dragStartPos = { lat: marker.getPosition().lat(), lng: marker.getPosition().lng() };
      isDraggingGroupRef.current = true;
      map.setOptions({ draggable: false });
    }));

    listeners.push(g.event.addListener(marker, 'drag', (e) => {
      if (!dragStartPos) return;
      const dLat = e.latLng.lat() - dragStartPos.lat;
      const dLng = e.latLng.lng() - dragStartPos.lng;
      // Imperatively move all other selected markers for smooth preview
      editMarkersRef.current.forEach((entry, idx) => {
        if (idx !== i && entry._isSelected) {
          const wp = waypoints[idx];
          if (wp) entry.marker.setPosition({ lat: wp.lat + dLat, lng: wp.lng + dLng });
        }
      });
    }));

    listeners.push(g.event.addListener(marker, 'dragend', (e) => {
      map.setOptions({ draggable: true });
      if (!dragStartPos) {
        isDraggingGroupRef.current = false;
        return;
      }
      const dLat = e.latLng.lat() - dragStartPos.lat;
      const dLng = e.latLng.lng() - dragStartPos.lng;
      dragStartPos = null;
      // Short timeout so the 'click' event triggered by drag-end gets ignored
      setTimeout(() => { isDraggingGroupRef.current = false; }, 50);
      onMarkerDragEnd(i, dLat, dLng);
    }));

    editMarkersRef.current.push({ marker, listeners, _isSelected: isSelected });
  });
}

function updateMarkerSelectionVisuals(markers, selectedIndices) {
  const g = window.google?.maps;
  if (!g) return;
  markers.forEach((entry, i) => {
    const isSelected = selectedIndices.includes(i);
    entry._isSelected = isSelected;
    entry.marker.setIcon(getLaneEditMarkerIcon(g, isSelected ? 'selected' : 'normal'));
    entry.marker.setZIndex(isSelected ? 2 : 1);
  });
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useLanePointEditor — rubber-band select + group drag move for lane waypoints.
 *
 * @param {Object} params
 * @param {google.maps.Map|null} params.map
 * @param {boolean} params.enabled             - laneEditMode active
 * @param {Array<{lat,lng}>} params.waypoints  - active lane waypoints
 * @param {number[]} params.selectedIndices    - currently selected indices
 * @param {Function} params.onSelectionChange  - (indices: number[]) => void
 * @param {Function} params.onWaypointsMoved   - (primaryIdx: number, deltaLat: number, deltaLng: number) => void
 */
export function useLanePointEditor({
  map,
  enabled,
  waypoints,
  selectedIndices,
  onSelectionChange,
  onWaypointsMoved,
  onWaypointInserted,
  onWaypointRemoved,
  onUndo,
  onRedo,
  onDeleteSelected,
}) {
  const editMarkersRef = useRef([]);
  const isDraggingGroupRef = useRef(false);
  const ctrlHeldRef = useRef(false);

  // ── Build / destroy markers when enabled or waypoints change ──────────────

  useEffect(() => {
    if (!map || !window.google?.maps || !enabled || !waypoints?.length) {
      // Clean up markers
      editMarkersRef.current.forEach(({ marker, listeners }) => {
        listeners.forEach((l) => window.google?.maps?.event?.removeListener?.(l));
        marker.setMap(null);
      });
      editMarkersRef.current = [];
      return;
    }

    buildLaneEditMarkers({
      map,
      waypoints,
      selectedIndices,
      isDraggingGroupRef,
      ctrlHeldRef,
      editMarkersRef,
      onMarkerClick: (index, shiftKey) => {
        if (shiftKey) {
          const already = selectedIndices.includes(index);
          const next = already
            ? selectedIndices.filter((i) => i !== index)
            : [...selectedIndices, index];
          onSelectionChange(next);
        } else {
          onSelectionChange([index]);
        }
      },
      onMarkerDragEnd: (primaryIdx, dLat, dLng) => {
        onWaypointsMoved(primaryIdx, dLat, dLng);
      },
      onMarkerRemove: (index) => {
        onWaypointRemoved?.(index);
      },
    });

    return () => {
      editMarkersRef.current.forEach(({ marker, listeners }) => {
        listeners.forEach((l) => window.google?.maps?.event?.removeListener?.(l));
        marker.setMap(null);
      });
      editMarkersRef.current = [];
    };
  // Rebuild markers whenever edit mode, waypoints list, or color changes.
  // selectedIndices is NOT in the dep array — selection visuals are handled separately below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, enabled, waypoints]);

  // ── Update selection visuals when selectedIndices changes ─────────────────

  useEffect(() => {
    if (!enabled) return;
    updateMarkerSelectionVisuals(editMarkersRef.current, selectedIndices);
  }, [selectedIndices, enabled]);

  // ── Rubber-band drag-select ───────────────────────────────────────────────

  useEffect(() => {
    if (!map || !enabled) return;

    const mapDiv = map.getDiv();
    if (!mapDiv) return;

    // Create rubber band overlay element
    const rubberBand = document.createElement('div');
    rubberBand.style.cssText =
      'position:absolute;border:2px solid #2563eb;background:rgba(37,99,235,0.12);' +
      'pointer-events:none;display:none;z-index:9999;box-sizing:border-box;';
    mapDiv.style.position = 'relative';
    mapDiv.appendChild(rubberBand);

    let rbStart = null; // { x, y } relative to mapDiv

    function getMapDivRect() {
      return mapDiv.getBoundingClientRect();
    }

    const onKeyDown = (e) => {
      if ((e.key === 'Control' || e.key === 'Meta') && !ctrlHeldRef.current) {
        ctrlHeldRef.current = true;
        map.setOptions({ draggable: false });
        mapDiv.style.cursor = 'crosshair';
      }
    };

    const onKeyUp = (e) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        ctrlHeldRef.current = false;
        // Cancel any in-progress rubber band
        rbStart = null;
        rubberBand.style.display = 'none';
        map.setOptions({ draggable: true });
        mapDiv.style.cursor = '';
      }
    };

    // Also re-enable map drag if window loses focus while Ctrl is held
    const onBlur = () => {
      if (ctrlHeldRef.current) {
        ctrlHeldRef.current = false;
        rbStart = null;
        rubberBand.style.display = 'none';
        map.setOptions({ draggable: true });
        mapDiv.style.cursor = '';
      }
    };

    const onMouseDown = (e) => {
      if (isDraggingGroupRef.current) return;
      // Only activate rubber band when Ctrl is held
      if (!ctrlHeldRef.current) return;
      // Skip marker SVGs / map controls
      const target = e.target;
      if (
        target?.tagName === 'svg' ||
        target?.tagName === 'path' ||
        target?.closest?.('[role="button"]') ||
        target?.closest?.('.gm-style-cc') ||
        target?.closest?.('.gm-control-active')
      ) return;

      const rect = getMapDivRect();
      rbStart = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      rubberBand.style.left = `${rbStart.x}px`;
      rubberBand.style.top = `${rbStart.y}px`;
      rubberBand.style.width = '0px';
      rubberBand.style.height = '0px';
      rubberBand.style.display = 'block';
    };

    const onMouseMove = (e) => {
      if (!rbStart) return;
      const rect = getMapDivRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      rubberBand.style.left = `${Math.min(cx, rbStart.x)}px`;
      rubberBand.style.top = `${Math.min(cy, rbStart.y)}px`;
      rubberBand.style.width = `${Math.abs(cx - rbStart.x)}px`;
      rubberBand.style.height = `${Math.abs(cy - rbStart.y)}px`;
    };

    const onMouseUp = (e) => {
      if (!rbStart) return;
      rubberBand.style.display = 'none';

      const rect = getMapDivRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;

      const selRect = {
        left: Math.min(cx, rbStart.x),
        right: Math.max(cx, rbStart.x),
        top: Math.min(cy, rbStart.y),
        bottom: Math.max(cy, rbStart.y),
      };
      rbStart = null;

      // Skip tiny drags (accidental clicks)
      const w = selRect.right - selRect.left;
      const h = selRect.bottom - selRect.top;
      if (w < 6 && h < 6) return;

      // Guard: projection may not be ready
      if (!map.getProjection()) return;

      const selected = [];
      editMarkersRef.current.forEach((entry, idx) => {
        const pos = entry.marker.getPosition();
        if (!pos) return;
        const px = latLngToMapPixel(map, pos);
        if (px && pixelInRect(px, selRect)) {
          selected.push(idx);
        }
      });

      if (selected.length > 0) {
        onSelectionChange(selected);
      }
    };

    // Click on the map background (not a marker, not after a pan) → deselect all
    const mapClickListener = window.google.maps.event.addListener(map, 'click', () => {
      if (!isDraggingGroupRef.current) {
        onSelectionChange([]);
      }
    });

    mapDiv.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);

    return () => {
      window.google.maps.event.removeListener(mapClickListener);
      mapDiv.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      // Ensure map dragging is restored on cleanup
      map.setOptions({ draggable: true });
      mapDiv.style.cursor = '';
      if (rubberBand.parentNode) rubberBand.parentNode.removeChild(rubberBand);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, enabled]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────

  useEffect(() => {
    if (!enabled) return;
    const handler = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;

      // Escape → clear selection
      if (e.key === 'Escape') {
        onSelectionChange([]);
        return;
      }
      // Ctrl+Z → undo
      if (ctrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        onUndo?.();
        return;
      }
      // Ctrl+Y or Ctrl+Shift+Z → redo
      if ((ctrl && e.key === 'y') || (ctrl && e.key === 'z' && e.shiftKey)) {
        e.preventDefault();
        onRedo?.();
        return;
      }
      // Delete / Backspace → delete selected
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        onDeleteSelected?.();
        return;
      }
      // Ctrl+A → select all
      if (ctrl && e.key === 'a') {
        e.preventDefault();
        if (waypoints?.length) {
          onSelectionChange(waypoints.map((_, i) => i));
        }
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, waypoints]);

  // ── Hover-insert polyline ─────────────────────────────────────────────────
  // Draws an invisible hit-area over the lane line so the user can click to
  // insert a new waypoint (same mechanism as the path hover-point system).

  useEffect(() => {
    if (!map || !window.google?.maps || !enabled || !waypoints?.length || waypoints.length < 2) return;
    if (!onWaypointInserted) return;

    const g = window.google.maps;
    const rawPath = waypoints.map((wp) => ({ lat: wp.lat, lng: wp.lng }));
    // Use the spline-interpolated path so the hit area follows the visible curve
    const path = rawPath.length >= 3
      ? catmullRomSpline(rawPath, SPLINE_SAMPLES_EDIT)
      : rawPath;

    // Wider invisible hit area so clicks are easy to land
    const hitArea = new g.Polyline({
      path,
      strokeColor: '#000000',
      strokeWeight: 20,
      strokeOpacity: 0,
      map,
      clickable: true,
      zIndex: 215,
    });

    // Hover ghost marker that snaps to nearest point on the line
    const hoverMarker = new g.Marker({
      position: path[0],
      map,
      visible: false,
      draggable: false,
      cursor: 'pointer',
      zIndex: 230,
      icon: {
        path: g.SymbolPath.CIRCLE,
        scale: 6,
        fillColor: '#ffffff',
        fillOpacity: 0.9,
        strokeColor: '#2563eb',
        strokeWeight: 2,
      },
    });

    // Snap helper: closest point on the polyline segments
    function closestPointOnSegment(ax, ay, bx, by, px, py) {
      const abx = bx - ax; const aby = by - ay;
      const t = Math.max(0, Math.min(1, ((px - ax) * abx + (py - ay) * aby) / (abx * abx + aby * aby)));
      return { lat: ax + t * abx, lng: ay + t * aby };
    }
    function snapToPath(lat, lng) {
      let best = null; let bestD = Infinity;
      for (let i = 0; i < path.length - 1; i++) {
        const p = closestPointOnSegment(path[i].lat, path[i].lng, path[i + 1].lat, path[i + 1].lng, lat, lng);
        const d = Math.hypot(p.lat - lat, p.lng - lng);
        if (d < bestD) { bestD = d; best = p; }
      }
      return best;
    }

    // Hide the ghost marker and suppress insertion near existing control points.
    // Radius is in pixel space so it stays consistent across zoom levels.
    const NODE_EXCLUSION_PX = 14;
    function isNearExistingNode(lat, lng) {
      const proj = map.getProjection();
      if (!proj) return false;
      const zoom = map.getZoom();
      const scale = Math.pow(2, zoom);
      const clickWorld = proj.fromLatLngToPoint(new g.LatLng(lat, lng));
      for (let i = 0; i < waypoints.length; i++) {
        const wpWorld = proj.fromLatLngToPoint(new g.LatLng(waypoints[i].lat, waypoints[i].lng));
        const dx = (clickWorld.x - wpWorld.x) * scale;
        const dy = (clickWorld.y - wpWorld.y) * scale;
        if (dx * dx + dy * dy < NODE_EXCLUSION_PX * NODE_EXCLUSION_PX) return true;
      }
      return false;
    }

    const mmListener = g.event.addListener(hitArea, 'mousemove', (e) => {
      if (ctrlHeldRef.current || isDraggingGroupRef.current) return;
      const lat = e.latLng?.lat(); const lng = e.latLng?.lng();
      if (lat == null) return;
      if (isNearExistingNode(lat, lng)) { hoverMarker.setVisible(false); return; }
      const pt = snapToPath(lat, lng);
      if (pt) { hoverMarker.setPosition(pt); hoverMarker.setVisible(true); }
    });
    const moListener = g.event.addListener(hitArea, 'mouseout', () => {
      hoverMarker.setVisible(false);
    });
    // Insert-node logic shared by both the hit-area polyline and the ghost marker
    function handleInsertClick(lat, lng) {
      if (ctrlHeldRef.current) return;
      if (isNearExistingNode(lat, lng)) return;
      const snapped = snapToPath(lat, lng);
      if (!snapped) return;
      const idx = findInsertIndexOnSpline(path, waypoints.length, SPLINE_SAMPLES_EDIT, snapped.lat, snapped.lng);
      if (idx != null) onWaypointInserted(idx, snapped.lat, snapped.lng);
    }

    const clListener = g.event.addListener(hitArea, 'click', (e) => {
      const lat = e.latLng?.lat(); const lng = e.latLng?.lng();
      if (lat != null) handleInsertClick(lat, lng);
    });

    // The ghost marker sits above the hit area (zIndex 230 > 215) and swallows
    // clicks. Give it its own click handler so clicking the white circle works.
    const hoverClListener = g.event.addListener(hoverMarker, 'click', () => {
      const pos = hoverMarker.getPosition();
      if (pos) handleInsertClick(pos.lat(), pos.lng());
    });

    return () => {
      g.event.removeListener(mmListener);
      g.event.removeListener(moListener);
      g.event.removeListener(clListener);
      g.event.removeListener(hoverClListener);
      hitArea.setMap(null);
      hoverMarker.setMap(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, enabled, waypoints]);
}
